import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import {
  Calendar,
  CalendarClock,
  CheckCircle2,
  Clock,
  AlertCircle,
  Loader2,
  TrendingUp,
} from 'lucide-react';
import { agendamentoAPI } from '../../services/api';
import { useChartTheme } from '../../components/ui';

/**
 * Dashboard do perfil MONITOR (Fase 26d, reformulado na Fase 30).
 *
 * Cards clicáveis:
 *   - Agendamentos hoje/amanhã → Agenda com filtro de data aplicado
 *   - Executadas/Pendentes hoje → Monitoramento
 * Gráficos:
 *   - Barras: agendamentos × execuções dia a dia do MÊS ATUAL
 *     (fonte: OS do ERP, mesma do Monitoramento; filtro por tipo)
 *   - Linha: crescimento acumulado da base de clientes — instalações
 *     EXECUTADAS mês a mês nos últimos 12 meses (fonte: Agenda/Mongo)
 */

type Stats = Awaited<ReturnType<typeof agendamentoAPI.dashboardStats>>;

function isoDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function MonitorDashboard() {
  const navigate = useNavigate();
  const chart = useChartTheme();
  const TOOLTIP_STYLE = {
    backgroundColor: chart.tooltipBg,
    border: `1px solid `,
    borderRadius: "8px",
    color: chart.tooltipText,
  };
  const [tipo, setTipo] = useState('');
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    agendamentoAPI
      .dashboardStats({ periodo: 'mes', tipo: tipo || undefined })
      .then((r) => {
        if (!cancelled) setStats(r);
      })
      .catch((err) => {
        const e = err as { response?: { data?: { message?: string } }; message?: string };
        if (!cancelled) setError(e.response?.data?.message ?? e.message ?? 'Erro ao carregar.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tipo]);

  const hoje = isoDay(new Date());
  const amanhaDate = new Date();
  amanhaDate.setDate(amanhaDate.getDate() + 1);
  const amanha = isoDay(amanhaDate);

  const chartPorDia = useMemo(() => {
    if (!stats) return [];
    return stats.labels.map((label, i) => ({
      label,
      Agendadas: stats.porDia.agendados[i] ?? 0,
      Executadas: stats.porDia.executados[i] ?? 0,
    }));
  }, [stats]);

  const chartCrescimento = useMemo(() => {
    const c = stats?.crescimentoBase;
    if (!c) return [];
    return c.labels.map((label, i) => ({
      label,
      Clientes: c.base[i] ?? 0,
    }));
  }, [stats]);

  const cards = [
    {
      label: 'Agendamentos hoje',
      value: stats?.cards.hoje,
      icon: <Calendar size={20} />,
      accent: 'text-primary bg-primary-soft/30',
      onClick: () => navigate(`/agendamento/agenda?dataInicio=${hoje}&dataFim=${hoje}`),
    },
    {
      label: 'Agendamentos amanhã',
      value: stats?.cards.amanha,
      icon: <CalendarClock size={20} />,
      accent: 'text-info bg-info-soft/30',
      onClick: () => navigate(`/agendamento/agenda?dataInicio=${amanha}&dataFim=${amanha}`),
    },
    {
      label: 'Executadas hoje',
      value: stats?.cards.executadasHoje,
      icon: <CheckCircle2 size={20} />,
      accent: 'text-success bg-success-soft/30',
      onClick: () => navigate('/agendamento/monitoramento'),
    },
    {
      label: 'Pendentes hoje',
      value: stats?.cards.pendentesHoje,
      icon: <Clock size={20} />,
      accent: 'text-warning bg-warning-soft/30',
      onClick: () => navigate('/agendamento/monitoramento'),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c) => (
          <button
            key={c.label}
            type="button"
            onClick={c.onClick}
            className="text-left bg-surface border border-line hover:border-primary-solid rounded-xl p-4 transition cursor-pointer"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted uppercase tracking-wider">{c.label}</span>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${c.accent}`}>
                {c.icon}
              </div>
            </div>
            <div className="text-2xl font-bold text-fg">
              {loading ? '…' : (c.value ?? '—')}
            </div>
          </button>
        ))}
      </div>

      {/* Filtro de tipo de serviço */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          className="rounded-lg bg-surface border border-line text-fg px-3 py-2 text-sm focus:border-primary focus:outline-none max-w-xs"
        >
          <option value="">Todos os tipos de serviço</option>
          {(stats?.tipos ?? []).map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        {loading && <Loader2 size={16} className="animate-spin text-primary" />}
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-danger-line/50 bg-danger-soft/20 px-4 py-3 text-sm text-danger">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Gráfico barras — agendamentos × execuções do mês atual */}
      <div className="bg-surface border border-line rounded-2xl p-6">
        <h2 className="text-lg font-semibold text-fg mb-4">
          Agendamentos × Execuções — mês atual
        </h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartPorDia} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={chart.grid} strokeDasharray="3 3" />
              <XAxis dataKey="label" stroke={chart.axis} fontSize={10} interval="preserveStartEnd" />
              <YAxis stroke={chart.axis} fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: chart.grid, fillOpacity: 0.5 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Agendadas" fill={chart.primary} radius={[3, 3, 0, 0]} />
              <Bar dataKey="Executadas" fill={chart.success} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Gráfico linha — crescimento da base de clientes (12 meses) */}
      <div className="bg-surface border border-line rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp size={18} className="text-success" />
          <h2 className="text-lg font-semibold text-fg">
            Crescimento da base de clientes
          </h2>
        </div>
        <p className="text-xs text-muted mb-4">
          Instalações executadas acumuladas mês a mês (últimos 12 meses).
        </p>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartCrescimento} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={chart.grid} strokeDasharray="3 3" />
              <XAxis dataKey="label" stroke={chart.axis} fontSize={10} />
              <YAxis stroke={chart.axis} fontSize={11} allowDecimals={false} domain={['auto', 'auto']} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Line
                type="monotone"
                dataKey="Clientes"
                stroke={chart.success}
                strokeWidth={2.5}
                dot={{ r: 2.5, fill: chart.success }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        {stats && chartCrescimento.length === 0 && !loading && (
          <p className="text-sm text-muted text-center py-4">
            Sem instalações executadas registradas ainda.
          </p>
        )}
      </div>
    </div>
  );
}
